// QuickStartAutomationsCard — one-click activation for the SMS
// workflows the operator has been sitting on in DRAFT status.
//
// Audit 2026-07-01: the strategy roll-up found 15 pre-built
// workflows already in the account, all with status:draft, 0
// enrolled, no trigger. Operators don't realize the infrastructure
// is built until they click through each one individually.
//
// This card lives in Settings → Comms & Calendar. Three states:
//   1. Loading — silent
//   2. All active OR no workflows exist — hide the card entirely
//   3. Drafts present — show count, list the top few, offer a
//      bulk "Activate all drafts" button
//
// Before activating we scan every step's template body for merge
// tokens (`{referral_amount}`, `{agent}`, `{agent_phone}`,
// `{booking_link}`) and cross-check the operator's settings. Any
// unset token blocks the bulk activation — the button surfaces a
// "Fix these first" list with jump-to-Settings links so the
// operator isn't sending "we pay up to {referral_amount} for
// referrals" as literal text.
//
// Runtime tokens like `{first}`, `{last}`, `{state}`, `{time_1}`,
// `{time_2}` are ignored — they're per-lead / per-appointment and
// resolve at send time, not activation time.

import React from 'react';
import { supabase } from '../lib/supabase';

// Tokens whose values MUST be present in settings for a workflow
// to render honestly. Keyed by token name → resolver that reads
// the settings object and returns the current value or ''.
const SETTINGS_TOKENS = {
  referral_amount: (s) => s?.referralPayoutAmount || s?.referralAmount || '',
  agent:           (s) => (s?.ownerName && s.ownerName !== 'You') ? s.ownerName : '',
  agent_name:      (s) => (s?.ownerName && s.ownerName !== 'You') ? s.ownerName : '',
  agent_phone:     (s) => s?.ownerPhone || s?.agentPhone || '',
  booking_link:    (s) => s?.bookingLink || '',
};

// Tokens ignored by the activation gate — resolved per-lead at
// send time, so blocking activation on them would be wrong.
const RUNTIME_TOKENS = new Set([
  'first', 'first_name',
  'last',  'last_name',
  'name',
  'state',
  'time_1', 'time_2',
  'carrier',
  'appointment_time', 'appt_time',
]);

// Friendly names + jump-to-Settings tab hints for the missing-token
// error surface.
const TOKEN_HINTS = {
  referral_amount: { label: 'Referral payout amount', where: 'Settings → Comms & Calendar → Referral asks' },
  agent:           { label: 'Your name',               where: 'Settings → Profile & Brand' },
  agent_name:      { label: 'Your name',               where: 'Settings → Profile & Brand' },
  agent_phone:     { label: 'Your phone',              where: 'Settings → Profile & Brand' },
  booking_link:    { label: 'Booking / calendar link', where: 'Settings → Comms & Calendar → Booking' },
};

function extractTokens(body) {
  if (!body) return [];
  const out = new Set();
  // Match {word}, {snake_case}, {first_name}, but not {{double}}.
  const re = /(?<!\{)\{([a-z][a-z0-9_]*)\}(?!\})/gi;
  let m;
  while ((m = re.exec(String(body))) != null) out.add(m[1].toLowerCase());
  return [...out];
}

export default function QuickStartAutomationsCard({ settings = {}, showToast = () => {} }) {
  const [state, setState] = React.useState('loading'); // loading | ready | hidden | activating
  const [drafts, setDrafts] = React.useState([]);      // [{id, name, unmet: string[]}]
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setState('loading'); setError(null);
    try {
      const { data: wfs, error: e1 } = await supabase
        .from('workflows')
        .select('id, name, active');
      if (e1) throw e1;
      const draftList = (wfs || []).filter(w => w.active === false);
      if (draftList.length === 0) { setState('hidden'); return; }

      // Pull steps + templates so we can scan merge tokens.
      const ids = draftList.map(w => w.id);
      const { data: steps } = await supabase
        .from('workflow_steps')
        .select('workflow_id, template_id')
        .in('workflow_id', ids);
      const templateIds = Array.from(new Set((steps || []).map(s => s.template_id).filter(Boolean)));
      const bodyById = {};
      if (templateIds.length > 0) {
        const { data: tpls } = await supabase
          .from('templates')
          .select('id, body')
          .in('id', templateIds);
        (tpls || []).forEach(t => { bodyById[t.id] = t.body || ''; });
      }
      const stepsByWf = {};
      (steps || []).forEach(s => {
        (stepsByWf[s.workflow_id] = stepsByWf[s.workflow_id] || []).push(s);
      });

      const analyzed = draftList.map(wf => {
        const tokens = new Set();
        for (const s of stepsByWf[wf.id] || []) {
          if (!s.template_id) continue;
          for (const t of extractTokens(bodyById[s.template_id])) tokens.add(t);
        }
        const unmet = [];
        for (const t of tokens) {
          if (RUNTIME_TOKENS.has(t)) continue;
          const resolver = SETTINGS_TOKENS[t];
          if (!resolver) continue; // unknown token → not our gate to enforce
          if (!String(resolver(settings) || '').trim()) unmet.push(t);
        }
        return { id: wf.id, name: wf.name, unmet: [...new Set(unmet)] };
      });

      setDrafts(analyzed);
      setState('ready');
    } catch (err) {
      setError(err?.message || String(err));
      setState('hidden');
    }
  }, [settings]);

  React.useEffect(() => { load(); }, [load]);

  const allUnmet = React.useMemo(() => {
    const s = new Set();
    for (const d of drafts) for (const u of d.unmet) s.add(u);
    return [...s];
  }, [drafts]);

  const blocked = allUnmet.length > 0;

  const activateAll = async () => {
    if (blocked) return;
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: `Activate ${drafts.length} draft workflow${drafts.length === 1 ? '' : 's'}?`,
      body: 'They\'ll start enrolling leads on their next trigger event.',
      confirmLabel: 'Activate all',
    }) : Promise.resolve(window.confirm(`Activate ${drafts.length} draft workflow${drafts.length === 1 ? '' : 's'} now?`)));
    if (!ok) return;
    setState('activating');
    try {
      const ids = drafts.map(d => d.id);
      const { error: upErr } = await supabase
        .from('workflows')
        .update({ active: true })
        .in('id', ids);
      if (upErr) throw upErr;
      showToast(`✓ Activated ${ids.length} workflow${ids.length === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      showToast(`Couldn't activate: ${err?.message || err}`, 'error');
      setState('ready');
    }
  };

  if (state === 'loading' || state === 'hidden') return null;
  if (error) return null; // silent — user still sees SmsWorkflowsPanel

  return (
    <div className="bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-xl p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-bold text-slate-900">Quick-start automations</h3>
          <p className="text-xs text-slate-600 mt-0.5">
            {drafts.length} SMS workflow{drafts.length === 1 ? '' : 's'} ready to activate. One click flips them all live.
          </p>
        </div>
        <button
          onClick={activateAll}
          disabled={blocked || state === 'activating'}
          className="bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold px-4 py-2 rounded-lg"
          title={blocked ? 'Fill the missing merge tokens below first' : 'Activate every draft workflow'}
        >
          {state === 'activating' ? 'Activating…' : `Activate all ${drafts.length}`}
        </button>
      </div>

      {blocked && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 space-y-1.5">
          <div className="text-xs font-bold text-amber-900">
            ⚠ Fix these first — templates reference tokens that aren't configured yet:
          </div>
          <ul className="text-xs text-amber-900 space-y-1">
            {allUnmet.map(t => {
              const hint = TOKEN_HINTS[t] || { label: `{${t}}`, where: 'Settings' };
              return (
                <li key={t} className="flex items-center gap-2">
                  <code className="bg-white border border-amber-300 rounded px-1.5 py-0.5 font-mono text-[11px]">{`{${t}}`}</code>
                  <span>{hint.label} — set in <strong>{hint.where}</strong></span>
                </li>
              );
            })}
          </ul>
          <div className="text-[11px] text-amber-800 pt-1">
            Activating anyway would send the literal placeholder text to clients. Fix the tokens above, then reload this page.
          </div>
        </div>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-slate-700 font-semibold hover:text-slate-900">
          Show the {drafts.length} drafts that will activate
        </summary>
        <ul className="mt-2 space-y-1">
          {drafts.map(d => (
            <li key={d.id} className="flex items-center gap-2 text-slate-700">
              <span className={`w-1.5 h-1.5 rounded-full ${d.unmet.length > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`}/>
              <span className="flex-1">{d.name}</span>
              {d.unmet.length > 0 && (
                <span className="text-[10px] text-amber-700">{d.unmet.length} unmet token{d.unmet.length === 1 ? '' : 's'}</span>
              )}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
